import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Switch,
  TextField,
} from '@mui/material';
import { APP_CONFIG } from '../../../../config/app.config';

interface WarehouseDataPreviewHeaderProps {
  previewData: {
    sheetNames?: string[];
    selectedSheet?: string;
  };
  firstRowIsHeader: boolean;
  headerRowIndex: number;
  onFirstRowIsHeaderChange: (checked: boolean) => void;
  onHeaderRowIndexChange: (index: number) => void;
  onSheetSelect?: (sheetName: string) => Promise<void>;
  canChangeHeaderRow?: boolean;
}

const WarehouseDataPreviewHeader: React.FC<WarehouseDataPreviewHeaderProps> = ({
  previewData,
  firstRowIsHeader,
  headerRowIndex,
  onFirstRowIsHeaderChange,
  onHeaderRowIndexChange,
  onSheetSelect,
  canChangeHeaderRow
}) => {

  const [selectedSheet, setSelectedSheet] = useState<string | undefined>(previewData.selectedSheet);

  const handleSheetSelect = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (onSheetSelect) {
      onSheetSelect(sheetName);
    }
  }

  useEffect(() => {
    setSelectedSheet(previewData.selectedSheet);
  }, [previewData.selectedSheet]);

  return (
    <Box sx={{ p: 3, pb: 0, backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
      {/* Excel Sheet Selection - Only for file data */}
      {previewData.sheetNames && previewData.sheetNames.length > 1 && (
        <Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {previewData.sheetNames.map((sheetName) => (
              <Button
                key={sheetName}
                variant={selectedSheet === sheetName ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSheetSelect(sheetName)}
                sx={{
                  borderColor: APP_CONFIG.ui.theme.colors.border,
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                {sheetName}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {/* Header row settings - Only for file data */}
      {(previewData.sheetNames && previewData.sheetNames.length > 1) || canChangeHeaderRow && (
        <Typography variant="subtitle2" sx={{ display: 'flex', color: 'text.primary', alignItems: 'center', gap: 1 }}>
          First row is the header row. <Switch size="small" checked={firstRowIsHeader} onChange={() => onFirstRowIsHeaderChange(!firstRowIsHeader)} />

          {!firstRowIsHeader && <Typography variant="body2">
            <TextField
              size="small"
              label="Header Row"
              type="number"
              value={headerRowIndex + 1}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  // Allow empty value temporarily
                  return;
                }
                const numValue = parseInt(value);
                if (!isNaN(numValue) && numValue > 0) {
                  onHeaderRowIndexChange(numValue - 1);
                }
              }}
              onBlur={(e) => {
                // When field loses focus, if it's empty, set to 1
                if (e.target.value === '') {
                  onHeaderRowIndexChange(0);
                }
              }}
              inputProps={{ min: 1 }}
              sx={{ width: '120px' }}
            />
          </Typography>}
        </Typography>
      )}


    </Box>
  );
};

export default WarehouseDataPreviewHeader; 